import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { uploadSuper, fileUrl } from "@/lib/upload";
import { Modal, Field, Select } from "@/components/Kit";
import { ImagePlus, Loader2 } from "lucide-react";

const EMPTY = { name: "", code: "", address: "", city: "", phone: "", email: "", logo_url: "", plan: "standard", status: "active", admin_name: "", admin_email: "", admin_password: "" };

export default function SchoolForm({ open, onClose, editing }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const { data: subs } = useQuery({ queryKey: ["sa-subs"], queryFn: async () => (await api.get("/superadmin/subscriptions")).data });
  const plans = subs?.plans || [];

  useEffect(() => { setForm(editing ? { ...EMPTY, ...editing, admin_password: "" } : EMPTY); }, [editing, open]);

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        const { name, code, address, city, phone, email, logo_url, plan, status, admin_password } = form;
        const body = { name, code, address, city, phone, email, logo_url, plan, status };
        if (admin_password) body.admin_password = admin_password;
        return (await api.patch(`/superadmin/schools/${editing.id}`, body)).data;
      }
      return (await api.post("/superadmin/schools", form)).data;
    },
    onSuccess: () => { toast.success(editing ? "School updated" : "School onboarded"); onClose(); qc.invalidateQueries({ queryKey: ["sa-schools"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const r = await uploadSuper(file, "logo"); setForm((f) => ({ ...f, logo_url: r.url })); toast.success("Logo uploaded"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setUploading(false); }
  };
  const valid = form.name && form.code && (editing || (form.admin_name && form.admin_email && form.admin_password));

  return (
    <Modal open={open} onClose={onClose} title={editing ? `Edit School — ${editing.code}` : "Onboard New School"} testid="school-modal">
      <div className="mb-4 flex items-center gap-4">
        <label className="group relative flex h-20 w-20 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-2xl border border-dashed border-slate-600 bg-slate-800/60 text-slate-400 hover:border-indigo-500" data-testid="f-logo-label">
          {form.logo_url ? <img src={fileUrl(form.logo_url)} alt="" className="h-full w-full object-cover" /> : uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={22} />}
          <span className="absolute inset-x-0 bottom-0 bg-black/60 py-0.5 text-center text-[10px] opacity-0 transition group-hover:opacity-100">Logo</span>
          <input type="file" accept="image/*" className="hidden" onChange={pickLogo} data-testid="f-logo" />
        </label>
        <div className="grid flex-1 grid-cols-2 gap-3">
          <div className="col-span-2"><Field label="School Name *" value={form.name} onChange={upd("name")} data-testid="f-name" /></div>
          <Field label="Code *" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="f-code" />
          <Select label="Plan" value={form.plan} onChange={upd("plan")} data-testid="f-plan">{plans.map((p) => <option key={p.id} value={p.id}>{p.name} · ₹{p.price.toLocaleString("en-IN")}/{p.billing}</option>)}</Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="City" value={form.city} onChange={upd("city")} data-testid="f-city" />
        <Field label="Phone" value={form.phone} onChange={upd("phone")} data-testid="f-phone" />
        <Field label="Email" type="email" value={form.email} onChange={upd("email")} data-testid="f-email" />
        {editing ? <Select label="Status" value={form.status} onChange={upd("status")} data-testid="f-status"><option value="active">Active</option><option value="pending">Pending</option><option value="suspended">Suspended</option></Select> : <div />}
        <div className="col-span-2"><Field label="Address" value={form.address} onChange={upd("address")} data-testid="f-address" /></div>
      </div>
      <div className="mt-4 border-t border-slate-800 pt-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{editing ? "School admin login" : "School admin account"}</p>
        <div className="grid grid-cols-2 gap-3">
          {!editing && <Field label="Admin Name *" value={form.admin_name} onChange={upd("admin_name")} data-testid="f-admin-name" />}
          {!editing && <Field label="Admin Email *" type="email" value={form.admin_email} onChange={upd("admin_email")} data-testid="f-admin-email" />}
          <div className={editing ? "col-span-2" : ""}><Field label={editing ? "Reset Admin Password (leave blank to keep)" : "Admin Password *"} type="text" value={form.admin_password} onChange={upd("admin_password")} data-testid="f-admin-password" /></div>
        </div>
      </div>
      <button className="ep-btn mt-5 w-full" disabled={save.isPending || uploading || !valid} onClick={() => save.mutate()} data-testid="submit-school-btn">
        {save.isPending ? "Saving…" : editing ? "Save Changes" : "Create School & Admin"}
      </button>
    </Modal>
  );
}
